import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Globe2, Mail, Lock, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await register(form.name, form.email, form.password);
    setLoading(false);
    if (res.ok) { toast.success("Account created — welcome aboard!"); navigate(next); }
    else toast.error(res.error);
  };

  return (
    <div className="min-h-[calc(100vh-68px)] grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 sm:p-12 order-2 lg:order-1">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <h1 className="font-heading font-black text-3xl text-slate-950">Create your account</h1>
          <p className="text-slate-500 mt-2">Join 2.4M travellers and unlock secret deals.</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            <div><Label>Full name</Label><div className="relative mt-1.5"><User className="w-4 h-4 absolute left-3 top-3 text-slate-400" /><Input data-testid="register-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="pl-9" placeholder="Jane Traveller" /></div></div>
            <div><Label>Email</Label><div className="relative mt-1.5"><Mail className="w-4 h-4 absolute left-3 top-3 text-slate-400" /><Input data-testid="register-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="pl-9" placeholder="you@email.com" /></div></div>
            <div><Label>Password</Label><div className="relative mt-1.5"><Lock className="w-4 h-4 absolute left-3 top-3 text-slate-400" /><Input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="pl-9" placeholder="Min. 6 characters" /></div></div>
            <Button disabled={loading} data-testid="register-submit" className="w-full rounded-full bg-brand hover:bg-brand-hover h-12 font-heading font-bold">{loading ? "Creating…" : "Create account"}</Button>
          </form>
          <p className="text-center text-sm text-slate-500 mt-6">Already have an account? <Link to="/login" className="text-brand font-bold">Sign in</Link></p>
        </motion.div>
      </div>
      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src="https://images.pexels.com/photos/35072454/pexels-photo-35072454.jpeg?auto=compress&cs=tinysrgb&w=1400" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/40" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <Globe2 className="w-10 h-10 mb-4" />
          <h2 className="font-heading font-black text-4xl leading-tight">The world is waiting.</h2>
          <p className="text-white/80 mt-3 text-lg">Save up to 15% extra with a free member account.</p>
        </div>
      </div>
    </div>
  );
}
